import { Descriptions, Divider, Grid } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import * as permissionKind from '../../../../../shared/permissions';
// eslint-disable-next-line max-len
import { type PorschePaymentModuleWithPermissionsSpecsFragment } from '../../../../api/fragments/PorschePaymentModuleWithPermissionsSpecs';
import ConsolePageWithHeader from '../../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import hasPermissions from '../../../../utilities/hasPermissions';
import useFormattedSimpleVersioning from '../../../../utilities/useFormattedSimpleVersioning';
import PorschePaymentModuleActions from './PorschePaymentModuleActions';
import PorschePaymentSettingsEmptyList from './PorschePaymentSettingsEmptyList';
import PorschePaymentSettingsTable from './PorschePaymentSettingsTable';

export type PorschePaymentModulePageProps = { module: PorschePaymentModuleWithPermissionsSpecsFragment };

const PorschePaymentModulePage = ({ module }: PorschePaymentModulePageProps) => {
    const { t } = useTranslation(['porschePaymentModuleDetails']);
    const navigate = useNavigate();
    const { updated, offset } = useFormattedSimpleVersioning({
        versioning: module.versioning,
        timeZone: module.company.timeZone,
    });
    const screens = Grid.useBreakpoint();

    const permissions = {
        hasUpdatePermission: hasPermissions(module.permissions, [permissionKind.updateModule]),
        hasDeletePermission: hasPermissions(module.permissions, [permissionKind.deleteModule]),
    };

    const content = (
        <Descriptions column={{ xs: 1, md: 2 }} bordered>
            <Descriptions.Item label={t('porschePaymentModuleDetails:content.displayName')}>
                {module.displayName}
            </Descriptions.Item>
            <Descriptions.Item label={t('porschePaymentModuleDetails:content.company')}>
                {module.company.displayName}
            </Descriptions.Item>
            <Descriptions.Item {...t('common:fields.updated', { returnObjects: true, offset })}>
                {updated}
            </Descriptions.Item>
        </Descriptions>
    );

    return (
        <ConsolePageWithHeader
            content={content}
            extra={<PorschePaymentModuleActions module={module} permissions={permissions} />}
            onBack={() => navigate('/admin/system/modules')}
            title={
                screens.md ? t('porschePaymentModuleDetails:title', { name: module.displayName }) : module.displayName
            }
        >
            <Divider>{t('porschePaymentModuleDetails:dividers.mainDetails')}</Divider>
            {module.paymentSettings.length === 0 ? (
                <PorschePaymentSettingsEmptyList module={module} />
            ) : (
                <PorschePaymentSettingsTable module={module} />
            )}
        </ConsolePageWithHeader>
    );
};

export default PorschePaymentModulePage;
